import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { IApiService } from 'core/api/services/api.interface';
import { UserApiService } from 'app/features/user/api/api-tokens';
import { UserStore } from 'app/features/user/store/user.store';

interface StateLookupResponse {
  zipCode: string;
  state: string;
}

/**
 * Frontend guard service that checks whether charging a cost of capital
 * is blocked for a given card billing ZIP code.
 *
 * Uses the profile's `blockCostOfCapitalForStates` and
 * `blockedCostOfCapitalStates` flags (from `UserStore`) plus a
 * lookup against the backend `GET /tax-rates/state-lookup?zipCode=`
 * endpoint which queries the TaxRates table.
 *
 * Results are cached per ZIP code for the lifetime of the service instance
 * to avoid redundant network calls when the same card is re-selected.
 */
@Injectable({ providedIn: 'root' })
export class CostOfCapitalGuardService {
  private readonly cache = new Map<string, string | null>();
  private readonly apiService = inject<IApiService>(UserApiService);
  private readonly userStore = inject(UserStore);

  /**
   * Returns `true` if charging a cost of capital is blocked for the given ZIP.
   *
   * Fail-open: returns `false` on any error, missing data, or ZIP not found in
   * the TaxRates table — so that a lookup failure never incorrectly blocks a
   * legitimate charge.
   */
  async isBlocked(zipCode: string | undefined | null): Promise<boolean> {
    return (await this.checkBlocked(zipCode)).blocked;
  }

  async checkBlocked(zipCode: string | undefined | null): Promise<{ blocked: boolean; state: string | null }> {
    const settings = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings;

    if (!settings?.blockCostOfCapitalForStates) return { blocked: false, state: null };

    const blockedList = (settings.blockedCostOfCapitalStates ?? []).map((s: string) => s.toUpperCase());
    if (blockedList.length === 0) return { blocked: false, state: null };

    if (!zipCode) return { blocked: false, state: null };

    const zip5 = zipCode.trim().substring(0, 5);
    if (!/^\d{5}$/.test(zip5)) return { blocked: false, state: null };

    if (!this.cache.has(zip5)) {
      const state = await this.fetchState(zip5);
      this.cache.set(zip5, state);
    }

    const state = this.cache.get(zip5) ?? null;
    const blocked = !!state && blockedList.includes(state.toUpperCase());
    return { blocked, state };
  }

  /** Clear the ZIP-to-state cache (e.g. on profile switch). */
  clearCache(): void {
    this.cache.clear();
  }

  private async fetchState(zip5: string): Promise<string | null> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<StateLookupResponse>('taxRateStateLookup', {
          params: { zipCode: zip5 },
        })
      );
      return response?.state?.toUpperCase() ?? null;
    } catch {
      return null;
    }
  }
}
